import { useState } from 'react'
import {
    Box,
    Chip,
    Grid,
    Tab,
    Tabs,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material'
import { Dashboard, Speed, Stream } from '@mui/icons-material'
import ReactECharts from 'echarts-for-react'

import { useGetRealtimeQuery } from '@/apis/v2Api'
import { useAppSelector } from '@/app/hooks'
import { useProjectId } from '@/hooks/useReportArgs'
import { Kpi, ReportHeader, ReportStatus, SectionCard, fmtNum } from '@/modules/reports/common'

import { RawEventsTab } from './RawEventsTab'

const POLL_MS = 10_000

/** v2 `/reports/realtime` (last N minutes), polled; raw rows from `/export`. */
export function RealtimePage() {
    const projectId = useProjectId()
    const subTenant = useAppSelector(s => s.project.subTenant)
    const [tab, setTab] = useState(0)
    const [minutes, setMinutes] = useState(30)

    const { data, isLoading, error, fulfilledTimeStamp } = useGetRealtimeQuery(
        { projectId, subTenant, minutes },
        { pollingInterval: POLL_MS, skip: tab !== 0, skipPollingIfUnfocused: true },
    )

    const chart = {
        tooltip: { trigger: 'axis' as const },
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: { type: 'category' as const, data: (data?.per_minute ?? []).map(p => p.t) },
        yAxis: { type: 'value' as const },
        series: [
            {
                name: 'Events',
                type: 'bar' as const,
                data: (data?.per_minute ?? []).map(p => p.events),
                itemStyle: { color: '#6366f1', borderRadius: [3, 3, 0, 0] },
            },
        ],
    }

    return (
        <Box sx={{ p: 3 }}>
            <ReportHeader
                title="Realtime"
                actions={
                    <>
                        <Chip
                            size="small"
                            color="success"
                            label={
                                fulfilledTimeStamp
                                    ? `Updated ${new Date(fulfilledTimeStamp).toLocaleTimeString()}`
                                    : 'Live'
                            }
                        />
                        {tab === 0 && (
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={minutes}
                                onChange={(_, v: number | null) => v && setMinutes(v)}
                            >
                                {[5, 30, 60].map(m => (
                                    <ToggleButton key={m} value={m} sx={{ textTransform: 'none' }}>
                                        {m} min
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        )}
                    </>
                }
            />

            <Tabs
                value={tab}
                onChange={(_, v: number) => setTab(v)}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
            >
                <Tab icon={<Dashboard />} iconPosition="start" label="Overview" />
                <Tab icon={<Stream />} iconPosition="start" label="Raw events" />
            </Tabs>

            {tab === 0 && (
                <ReportStatus
                    isLoading={isLoading}
                    error={error}
                    isEmpty={!!data && data.events === 0}
                    emptyIcon={<Speed />}
                    emptyTitle={`No events in the last ${minutes} minutes`}
                    emptyDescription="This view refreshes every 10 seconds."
                >
                    {data && (
                        <>
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <Kpi
                                        title="Active visitors"
                                        value={fmtNum(data.active_visitors)}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6, sm: 4 }}>
                                    <Kpi
                                        title="Active sessions"
                                        value={fmtNum(data.active_sessions)}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6, sm: 4 }}>
                                    <Kpi title="Events" value={fmtNum(data.events)} />
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 7 }}>
                                    <SectionCard title="Events per minute">
                                        <ReactECharts option={chart} style={{ height: 280 }} />
                                    </SectionCard>
                                </Grid>
                                <Grid size={{ xs: 12, md: 5 }}>
                                    <SectionCard title="Top pages right now">
                                        {data.top_pages.length === 0 && (
                                            <Typography variant="body2" color="text.secondary">
                                                No page views yet
                                            </Typography>
                                        )}
                                        {data.top_pages.map(p => (
                                            <Box
                                                key={p.path}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    py: 1,
                                                    borderBottom: '1px solid',
                                                    borderColor: 'divider',
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    noWrap
                                                    sx={{ maxWidth: '75%' }}
                                                >
                                                    {p.path || '/'}
                                                </Typography>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {fmtNum(p.sessions)}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </SectionCard>
                                </Grid>
                            </Grid>
                        </>
                    )}
                </ReportStatus>
            )}

            {tab === 1 && <RawEventsTab />}
        </Box>
    )
}
